import type { ClientCreateCustomerRoutedLoadInput } from '@/types'
import type {
  ApiCustomerRoutedEquipmentDTO,
  ApiCustomerRoutedHandlingUnitDTO,
} from '@/types/api-dto'

import {
  buildApiHandlingUnitFromFormCommodity,
  modeToWorkflow,
} from '../commodities/buildApiHandlingUnit'
import type { FormCommodity } from '../commodities/FormCommodity'
import { buildHandlingUnitDTO } from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import type { ShipmentLocationFormModel } from '../shipment-location-form/types'
import { toApiLocation } from '../shipment-location-form/types'

type CustomerRoutedEquipmentFields = Omit<
  ApiCustomerRoutedEquipmentDTO,
  'items' | 'declaredValueCurrency'
> & {
  declaredValueCurrency?: ApiCustomerRoutedEquipmentDTO['declaredValueCurrency']
  items: ApiCustomerRoutedHandlingUnitDTO[]
}

function buildCustomerRoutedEquipment(
  args: CustomerRoutedEquipmentFields,
): ApiCustomerRoutedEquipmentDTO {
  const {
    loadId,
    declaredValue,
    alternateTypes = [],
    declaredValueCurrency = 'usd',
    ...equipment
  } = args

  return {
    ...equipment,
    alternateTypes,
    declaredValueCurrency,
    ...(declaredValue !== undefined && { declaredValue }),
    ...(loadId && { loadId }),
  }
}

export function buildCustomerRoutedEquipmentFromFormItems(
  args: Omit<CustomerRoutedEquipmentFields, 'items'> & {
    items: FormCommodity[]
  },
): ApiCustomerRoutedEquipmentDTO {
  const { items, mode, ...equipment } = args

  return buildCustomerRoutedEquipment({
    ...equipment,
    mode,
    items: items.map(item => buildApiHandlingUnitFromFormCommodity(item, mode)),
  })
}

export function buildCustomerRoutedEquipmentFromHandlingUnits(
  args: Omit<CustomerRoutedEquipmentFields, 'items' | 'weight'> & {
    items: LoadHandlingUnitFormModel[]
  },
): ApiCustomerRoutedEquipmentDTO {
  const { items, mode, ...equipment } = args
  const mappedItems = items.map(item =>
    buildHandlingUnitDTO(modeToWorkflow(mode))(item),
  )

  return buildCustomerRoutedEquipment({
    ...equipment,
    mode,
    items: mappedItems,
    weight: mappedItems.reduce(
      (sum, item) => sum + Number(item.weight ?? 0),
      0,
    ),
  })
}

export function buildCustomerRoutedLoadPayload(
  payload: Omit<ClientCreateCustomerRoutedLoadInput, 'locations'> & {
    locations: ShipmentLocationFormModel[]
  },
): ClientCreateCustomerRoutedLoadInput {
  const { locations, ...rest } = payload
  return {
    ...rest,
    locations: locations.map(toApiLocation),
  }
}
